import React from "react";
import { Link } from "react-router-dom";
import { Ban, Banknote, ChevronLeft } from "lucide-react";
import { useSale } from "./hooks";
import { getSalePaymentSections, productTableConfig } from "./utils";
import Table from "../../../components/Core/Tables/Table";
import { EmptyData } from "../../../components/Core/EmptyData/EmptyData";
import LoaderPage from "../../../components/LoaderPage/LoaderPage";
import {
  AppOrderInfo,
  PartialCancellationModal,
  SaleHeader,
  SaleSummary,
  PaymentDetail,
  CompleteSaleModal,
  SendToSessionModal,
} from "./components";
import { ErrorModalContext } from "../../../components/AlertsModal/AlertsModal";
import { CancellationModal } from "../../../components/CancellationModal/CancellationModal";
import { ProductChangeModal } from "../../../components/ProductChange/ProductChangeModal";
import {
  changeSaleProductsService,
  getSaleProductChangeOptionsService,
} from "../../../services/sales";
import { WarningModal } from "../../../components/WarningModal/WarningModal";
import { SaleStatus } from "../../../interfaces/sales";
import { isExternalOrderInOpenBill, isPendingExternalOrder, isTipAdjustmentSale } from "../../../utils/sales";
import { getSellerNameWithAppOrderType } from "../../../translates/sales/sales";
import { getDistinctSecondarySeller } from "../../../utils/getDistinctSecondarySeller";

const SaleDetail = () => {
  const {
    cantRefund,
    isLoading,
    isLoadingRefund,
    isCompleting,
    isConfirmingCash,
    hasCashPayment,
    makeRefund,
    completeSale,
    confirmCashPayment,
    refreshData,
    downloadPdf,
    isDownloadingPdf,
    sale,
    setShowRefundModal,
    showRefundModal,
    requiresSpei,
    cancelPhoneNumber,
    setCancelPhoneNumber,
    cancelComment,
    setCancelComment,
    cardRefundTotal,
    cancelRestockDecisions,
    setCancelRestockDecision,
    requestCancelSale,
    showCancelPreparationConfirm,
    confirmCancelSaleInPreparation,
    dismissCancelPreparationConfirm,
  } = useSale();
  const [showPartialCancelModal, setShowPartialCancelModal] = React.useState(false);
  const [showSendToSessionModal, setShowSendToSessionModal] = React.useState<boolean>(false);
  const { showAlert } = React.useContext(ErrorModalContext);
  const [showProductChangeModal, setShowProductChangeModal] = React.useState(false);
  const [showConfirmCashModal, setShowConfirmCashModal] = React.useState<boolean>(false);
  const [showCompleteModal, setShowCompleteModal] = React.useState(false);

  /**
   * YOS-10255: the seller field names the primary session and appends the seller key holder, the same
   * pair the sales list and the bill detail already show. Dropped when both resolve to the same person,
   * see `getDistinctSecondarySeller`.
   */
  const sellerLabel: string = React.useMemo(() => {
    if (!sale) {
      return "";
    }
    const primary: string = getSellerNameWithAppOrderType(
      sale.seller.name,
      sale.app_order_type,
    );
    const secondary: string | undefined = getDistinctSecondarySeller(
      sale.seller.name,
      sale.secondary_seller?.name,
    );
    return secondary ? `${primary} · ${secondary}` : primary;
  }, [sale]);

  return isLoading ? (
    <LoaderPage />
  ) : sale ? (
    <div className="space-y-6">
      <CancellationModal
        closeModal={() => setShowRefundModal(false)}
        isLoading={isLoadingRefund}
        makeRefund={requestCancelSale}
        open={showRefundModal}
        orderCode={sale.code}
        requiresPhoneNumber={requiresSpei}
        cardRefundTotal={cardRefundTotal}
        onPhoneNumberChange={setCancelPhoneNumber}
        comment={cancelComment}
        onCommentChange={setCancelComment}
        commentRequired={sale.payments.length > 0}
        restockProducts={sale.products.map((p) => ({
          uuid: p.uuid,
          name: p.product,
          quantity: p.quantity,
          price: p.price,
          has_supplies: p.has_supplies,
        }))}
        restockDecisions={cancelRestockDecisions}
        onRestockChange={setCancelRestockDecision}
      />
      {/* YOS-10255: second confirmation when the sale has products already in preparation */}
      <WarningModal
        open={showCancelPreparationConfirm}
        onClose={dismissCancelPreparationConfirm}
        title="Confirmar cancelación"
        messageTitle="Advertencia importante:"
        message={
          <p>
            Este o estos productos ya se encuentran en preparación, ¿estás seguro que deseas cancelar?
          </p>
        }
        confirmLabel="Sí, cancelar"
        discardLabel="No"
        confirmButtonClassName="bg-red-600 hover:bg-red-700"
        ConfirmIcon={Ban}
        onConfirm={confirmCancelSaleInPreparation}
        isLoading={isLoadingRefund}
      />
      <PartialCancellationModal
        open={showPartialCancelModal}
        closeModal={() => setShowPartialCancelModal(false)}
        products={sale.products}
        payments={sale.payments}
        saleUuid={sale.uuid}
        saleCode={sale.code}
        onSuccess={refreshData}
      />
      <ProductChangeModal
        open={showProductChangeModal}
        closeModal={() => setShowProductChangeModal(false)}
        entityLabel="venta"
        entityCode={sale.code}
        loadOptions={() => getSaleProductChangeOptionsService(sale.uuid)}
        submitChanges={(body) => changeSaleProductsService(sale.uuid, body)}
        onSuccess={refreshData}
      />
      <SaleHeader
        canRefund={cantRefund.length === 0 && (sale.status !== SaleStatus.Cancelada && sale.status !== SaleStatus.Reembolsada && sale.status !== SaleStatus["Reembolso pendiente"] && sale.status !== SaleStatus.Expirada)}
        code={sale.code}
        hasCashPayment={hasCashPayment}
        openRefundModal={() => setShowRefundModal(true)}
        openPartialCancelModal={() => setShowPartialCancelModal(true)}
        openProductChangeModal={() => setShowProductChangeModal(true)}
        onConfirmCashPayment={() => setShowConfirmCashModal(true)}
        onComplete={() => setShowCompleteModal(true)}
        onDownloadPdf={downloadPdf}
        isDownloadingPdf={isDownloadingPdf}
        status={sale.status}
        isPendingExternalOrder={isPendingExternalOrder(sale)}
        isExternalOrderInOpenBill={isExternalOrderInOpenBill(sale)}
        isTipAdjustment={isTipAdjustmentSale(sale)}
        bill={sale.bill}
        onSendToSession={() => setShowSendToSessionModal(true)}
      />
      <SendToSessionModal
        open={showSendToSessionModal}
        onClose={() => setShowSendToSessionModal(false)}
        saleUuid={sale.uuid}
        saleCode={sale.code}
        currentBillName={isExternalOrderInOpenBill(sale) ? sale.bill?.name : undefined}
        onSent={async (message: string) => {
          setShowSendToSessionModal(false);
          showAlert(message || "La cuenta se mandó a la sesión", "success");
          // Reloading brings the sale's bill, which links it and turns the button into "Cambiar de sesión"
          await refreshData();
        }}
      />
      <CompleteSaleModal
        open={showCompleteModal}
        onClose={() => setShowCompleteModal(false)}
        sale={sale}
        allowSpei={isPendingExternalOrder(sale)}
        isLoading={isCompleting}
        onConfirm={async (payments) => {
          // YOSHI-10935: on an error (e.g. a tracking key already registered) the modal stays open so the
          // captured transfer is not lost
          const isCompleted: boolean = await completeSale({ payments });
          if (isCompleted) {
            setShowCompleteModal(false);
          }
        }}
      />
      <WarningModal
        open={showConfirmCashModal}
        onClose={() => setShowConfirmCashModal(false)}
        title="Confirmar pago en efectivo"
        messageTitle="Confirmación de pago"
        message={
          <p>
            ¿Estás seguro de confirmar el pago en efectivo de la orden{" "}
            <span className="font-bold text-black">{sale.code}</span>?
            Esta acción marcará la venta como pagada.
          </p>
        }
        confirmLabel="Confirmar pago"
        confirmButtonClassName="bg-green-600 hover:bg-green-700"
        ConfirmIcon={Banknote}
        onConfirm={async () => {
          await confirmCashPayment();
          setShowConfirmCashModal(false);
        }}
        isLoading={isConfirmingCash}
      />
      <SaleSummary
        concession={sale.concession.name}
        date={sale.sold_at}
        event={sale.event.name}
        seller={sellerLabel}
        subtotal={sale.subtotal}
        tip={sale.tip}
        reassignedTip={sale.reassigned_tip}
        appServiceFee={sale.app_service_fee}
        total={sale.total}
      />
      {sale.app_order && <AppOrderInfo appOrder={sale.app_order} />}
      {sale.comments && sale.comments.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-lg font-medium text-gray-800">
            {sale.comments.length === 1 ? 'Comentario' : 'Comentarios'}
          </h2>
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            {sale.comments.map((comment, index) => (
              <p key={index} className="text-sm text-gray-700">{comment}</p>
            ))}
          </div>
        </div>
      )}
      <div className="space-y-3">
        <h2 className="text-lg font-medium text-gray-800">Productos</h2>
        <Table
          columns={productTableConfig}
          data={sale.products}
          renderAfterLoading={true}
        />
      </div>
      {sale.payments.length > 0 && (
        <div className="flex flex-col gap-3">
          <h2 className="text-lg font-medium text-gray-800">Pagos</h2>
          <div className="space-y-5">
            {/*
              YOSHI-10444: the fields of every payment now come from `getSalePaymentSections`, the
              same source the PDF export prints, so both stay in sync.
            */}
            {getSalePaymentSections(sale).map((section, index) => (
              <div
                key={`${section.code}-${index}`}
                className="rounded-xl border border-gray-200 bg-white"
              >
                <PaymentDetail
                  title={section.title}
                  code={section.code}
                  total={section.total}
                  status={section.status}
                  fields={section.fields}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  ) : (
    <>
      <Link
        to="/panel/ordenes"
        className="flex w-fit items-center gap-1 text-gray-500 transition-colors duration-300 hover:text-gray-900"
      >
        <ChevronLeft className="h-4 w-4" />
        <span className="text-sm">Volver a Órdenes</span>
      </Link>
      <EmptyData
        title="No se encontró información de la venta"
        description="Intenta nuevamente con otra venta"
      />
    </>
  );
};

export default SaleDetail;
